import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { io } from 'socket.io-client';

const TournamentDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // --- IDENTITY & STATE ---
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (storedUser.id || storedUser._id)?.toString();
    const userRole = storedUser.role || 'player';
    
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [manualPlayerName, setManualPlayerName] = useState('');
    const [isAddingPlayer, setIsAddingPlayer] = useState(false);
    const [addPlayerError, setAddPlayerError] = useState('');

    useEffect(() => {
        fetchTournament();
        // --- THE REAL-TIME MAGIC ---
        // 1. Connect to your backend server (it automatically handles the URL)
        const socketUrl = process.env.REACT_APP_API_URL 
            ? process.env.REACT_APP_API_URL.replace('/api', '') 
            : 'http://localhost:5000';
            
        const socket = io(socketUrl);

        // 2. Tell the server to put us in this specific tournament's "Room"
        socket.emit('joinTournament', id);

        // 3. Listen for the server to shout "tournamentUpdated!"
        socket.on('tournamentUpdated', () => {
            console.log('⚡ Live update received! Refreshing data...');
            fetchTournament(); // Silently pull the fresh data in the background!
        });

        // 4. Clean up the connection if they leave the page
        return () => {
            socket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchTournament = async () => {
        try {
            const { data } = await api.get(`/tournaments/${id}`);
            setTournament(data);
        } catch (err) {
            console.error("Error fetching tournament:", err);
            alert("Tournament not found.");
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center font-black text-blue-600 text-xl animate-pulse">Loading Lobby...</div>;
    if (!tournament) return null;

    // --- PERMISSIONS ---
    // Strict check: You must be an Admin, OR (You are a Master AND you created this specific tournament)
    const masterIdStr = tournament.masterId ? (tournament.masterId._id || tournament.masterId).toString() : '';
    const currentUserIdStr = userId ? userId.toString() : '';
    const isMaster = userRole === 'admin' || (userRole === 'master' && masterIdStr && currentUserIdStr && masterIdStr === currentUserIdStr);
    const maxPlayers = tournament.registration?.maxPlayers || 32;
    const currentPlayers = tournament.players?.length || 0;
    const fillPercentage = Math.min((currentPlayers / maxPlayers) * 100, 100);
    
    // Check if the current user has already applied or is already in the roster
    const hasApplied = tournament.applicants?.some(a => (a.user?._id || a.user)?.toString() === currentUserIdStr);
    const isInRoster = tournament.players?.some(p => p.userId && p.userId.toString() === currentUserIdStr);

    // --- ACTION HANDLERS ---
    const handleAddManualPlayer = async (e) => {
        if (e) e.preventDefault();
        const trimmed = manualPlayerName.trim();
        if (!trimmed) {
            setAddPlayerError('Please enter a player name');
            return;
        }

        setIsAddingPlayer(true);
        setAddPlayerError('');
        try {
            await api.post(`/tournaments/${id}/manual-player`, { name: trimmed });
            setManualPlayerName('');
            fetchTournament();
        } catch (err) {
            setAddPlayerError(err.response?.data?.error || 'Failed to add player');
        } finally {
            setIsAddingPlayer(false);
        }
    };
  const handleJoin = async () => {
        try {
            // PHASE 5 FIX: Pass the username so the Master sees who is applying!
            await api.post(`/tournaments/${id}/join`, { username: storedUser.username || 'Player' });
            fetchTournament(); // Refresh the lobby
        } catch (err) {
            alert(err.response?.data?.error || "Error joining tournament");
        }
    };

    const handleApprove = async (applicantId) => {
        try {
            // We will build this backend route next!
            await api.post(`/tournaments/${id}/approve`, { applicantId });
            fetchTournament();
        } catch (err) {
            alert(err.response?.data?.error || "Error approving player");
        }
    };
    const handleKickPlayer = async (playerId, playerName) => {
        if (!window.confirm(`Are you sure you want to kick ${playerName} from the tournament?`)) return;
        try {
            await api.delete(`/tournaments/${id}/players/${playerId}`);
            fetchTournament();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to kick player.");
        }
    };

    const startTournament = async () => {
        try {
            await api.post(`/tournaments/${id}/start`);
            navigate(`/tournament/${id}/standings`); // Go to live brackets!
        } catch (err) {
            alert(err.response?.data?.error || "Failed to start tournament");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto space-y-8">
                
                {/* --- HEADER BANNER --- */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="bg-blue-100 text-blue-700 font-black px-3 py-1 rounded-lg text-xs uppercase tracking-widest">
                                {tournament.game}
                            </span>
                            <span className="bg-gray-100 text-gray-600 font-bold px-3 py-1 rounded-lg text-xs uppercase tracking-widest">
                                {tournament.settings?.format}
                            </span>
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight">{tournament.name}</h1>
                        {tournament.description && <p className="text-gray-500 mt-2 font-medium">{tournament.description}</p>}
                    </div>
                    
                    {/* Status Badge */}
                    <div className={`px-6 py-3 rounded-2xl border-2 font-black text-lg uppercase tracking-widest ${
                        tournament.status === 'setup' || tournament.status === 'draft' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                        tournament.status === 'active' ? 'bg-green-50 border-green-200 text-green-700 animate-pulse' :
                        'bg-gray-100 border-gray-200 text-gray-500'
                    }`}>
                        {tournament.status === 'setup' ? 'Registration Open' : tournament.status}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    
                    {/* --- LEFT COLUMN: EVENT DETAILS --- */}
                    <div className="md:col-span-2 space-y-8">
                        
                        {/* 1. Info Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                                <span className="text-2xl mb-1">📅</span>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date</span>
                                <span className="font-black text-gray-800">{tournament.date ? new Date(tournament.date).toLocaleDateString() : 'TBA'}</span>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                                <span className="text-2xl mb-1">⏰</span>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Time</span>
                                <span className="font-black text-gray-800">{tournament.time || 'TBA'}</span>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                                <span className="text-2xl mb-1">📍</span>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Location</span>
                                <span className="font-black text-gray-800 truncate w-full">{tournament.location || 'TBA'}</span>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                                <span className="text-2xl mb-1">🎟️</span>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Entry Fee</span>
                                <span className="font-black text-green-600">{tournament.registration?.entryFee || 'Free'}</span>
                            </div>
                        </div>

                        {/* 2. The Roster */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                <h3 className="font-black text-gray-800 text-lg">Official Roster</h3>
                                <span className="font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">{currentPlayers} Players</span>
                            </div>
                            <div className="p-2">
                                {currentPlayers === 0 ? (
                                    <p className="p-8 text-center text-gray-400 font-bold italic">No players have been approved yet.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-4">
                                        {tournament.players.map((p, i) => {
                                            const isGuest = p.id?.startsWith('guest-') || (!p.userId && !p.id?.startsWith('bot-'));
                                            const isBot = p.id?.startsWith('bot-');
                                            return (
                                                <div key={p.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border border-gray-100 group">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${
                                                            isGuest ? 'bg-amber-100 text-amber-800' :
                                                            isBot ? 'bg-purple-100 text-purple-800' :
                                                            'bg-blue-100 text-blue-700'
                                                        }`}>
                                                            {i + 1}
                                                        </div>
                                                        <div className="truncate">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="font-bold text-gray-800 truncate">{p.name}</span>
                                                                {isGuest && (
                                                                    <span className="text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                                        Guest
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* KICK BUTTON (Only visible to Master during setup) */}
                                                    {isMaster && tournament.status === 'setup' && (
                                                        <button 
                                                            onClick={() => handleKickPlayer(p.id, p.name)}
                                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors opacity-0 group-hover:opacity-100 shrink-0 ml-2"
                                                            title="Remove Player"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: REGISTRATION & MASTER CONTROLS --- */}
                    <div className="space-y-6">
                        
                        {/* Capacity Tracker */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6">
                            <div className="flex justify-between items-end mb-2">
                                <span className="font-black text-gray-800">Capacity</span>
                                <span className="text-sm font-bold text-gray-500">{currentPlayers} / {maxPlayers}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3 mb-6 overflow-hidden">
                                <div className="bg-blue-600 h-3 rounded-full transition-all duration-1000" style={{ width: `${fillPercentage}%` }}></div>
                            </div>

                            {/* PLAYER ACTIONS */}
                            {!isMaster && tournament.status === 'setup' && (
                                <div className="space-y-3">
                                    {isInRoster ? (
                                        <div className="w-full bg-green-50 text-green-700 font-black py-4 rounded-xl text-center border-2 border-green-200">
                                            ✅ You are in the roster!
                                        </div>
                                    ) : hasApplied ? (
                                        <div className="w-full bg-yellow-50 text-yellow-700 font-black py-4 rounded-xl text-center border-2 border-yellow-200">
                                            ⏳ Application Pending...
                                        </div>
                                    ) : (
                                        <button onClick={handleJoin} disabled={currentPlayers >= maxPlayers} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-black py-4 rounded-xl shadow-lg shadow-blue-100 transition-transform active:scale-95">
                                            {currentPlayers >= maxPlayers ? 'Tournament Full' : 'JOIN TOURNAMENT'}
                                        </button>
                                    )}
                                </div>
                            )}

{/* MASTER ACTIONS */}
{isMaster && tournament.status === 'setup' && (
    <div className="space-y-4">
        {/* ADD MANUAL / GUEST PLAYER */}
        <div className="pt-2 border-t border-gray-100">
            <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                <span>➕</span> Add Manual / Guest Player
            </h4>
            <form onSubmit={handleAddManualPlayer} className="space-y-2">
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Enter player name..."
                        value={manualPlayerName}
                        onChange={(e) => {
                            setManualPlayerName(e.target.value);
                            if (addPlayerError) setAddPlayerError('');
                        }}
                        disabled={currentPlayers >= maxPlayers || isAddingPlayer}
                        className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all disabled:opacity-50"
                    />
                    <button
                        type="submit"
                        disabled={currentPlayers >= maxPlayers || isAddingPlayer || !manualPlayerName.trim()}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-black px-4 py-2.5 rounded-xl text-sm transition-all shadow-md shadow-blue-100 active:scale-95 whitespace-nowrap"
                    >
                        {isAddingPlayer ? 'Adding...' : 'Add'}
                    </button>
                </div>
                {addPlayerError && (
                    <p className="text-xs font-bold text-red-500 px-1">{addPlayerError}</p>
                )}
                <p className="text-[11px] text-gray-400 font-medium px-1">
                    Adds a temporary guest player not linked to a user account.
                </p>
            </form>
        </div>

        <button onClick={startTournament} className="w-full bg-green-600 hover:bg-green-700 text-white font-black py-4 rounded-xl shadow-lg shadow-green-100 transition-transform active:scale-95 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            START TOURNAMENT
        </button>
    </div>
)}
                            
                            {/* IF ALREADY ACTIVE */}
                            {tournament.status !== 'setup' && (
                                <button onClick={() => navigate(`/tournament/${id}/standings`)} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-xl shadow-lg shadow-blue-100 transition-transform active:scale-95 mt-4">
                                    VIEW LIVE STANDINGS
                                </button>
                            )}
                        </div>

                        {/* MASTER APPLICANT QUEUE */}
                        {isMaster && tournament.status === 'setup' && (
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                                    <h3 className="font-black text-gray-800 text-sm">Waiting List ({tournament.applicants?.length || 0})</h3>
                                </div>
                                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                                    {(!tournament.applicants || tournament.applicants.length === 0) ? (
                                        <p className="p-4 text-center text-xs text-gray-400 font-bold">No pending applications.</p>
                                    ) : (
                                        tournament.applicants.map(app => (
                                            <div key={app._id} className="p-4 flex justify-between items-center hover:bg-gray-50 transition-colors">
                                                <span className="font-bold text-gray-700 text-sm">{app.username}</span>
                                                <button onClick={() => handleApprove(app.user)} className="bg-blue-100 hover:bg-blue-600 text-blue-700 hover:text-white px-3 py-1.5 rounded-lg text-xs font-black transition-colors">
                                                    Approve
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default TournamentDetails;